import React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import moment from 'moment';
import { RichText, Typography } from '_atoms';
import { TIME_ZONE } from 'constants/constants';
import type { CandidateMergeData } from './types';

type FieldRecord = CandidateMergeData;

type FieldConfig = {
  field: string;
  title: string;
  required: boolean;
  isHeader: boolean;
  render: (
    value: unknown,
    record?: FieldRecord,
    field?: string,
    expandedRows?: Record<string, boolean>,
    toggleExpand?: (key: string) => () => void
  ) => React.ReactNode;
};

export const useFields = (): FieldConfig[] => {
  const { t } = useTranslation();
  return [
    {
      field: '_profile',
      title: t('translation:merge_profile.label_profile'),
      required: false,
      isHeader: true,
      render: value => value,
    },
    {
      field: 'first_name_latin',
      title: t('translation:merge_profile.label_first_name_latin'),
      required: true,
      isHeader: false,
      render: value => value,
    },
    {
      field: 'last_name_latin',
      title: t('translation:merge_profile.label_last_name_latin'),
      required: true,
      isHeader: false,
      render: value => value,
    },
    {
      field: 'first_name_local',
      title: t('translation:merge_profile.label_first_name_local'),
      required: false,
      isHeader: false,
      render: value => value,
    },
    {
      field: 'last_name_local',
      title: t('translation:merge_profile.label_last_name_local'),
      required: false,
      isHeader: false,
      render: value => value,
    },
    {
      field: 'country',
      title: t('translation:merge_profile.label_country_city'),
      required: true,
      isHeader: false,
      render: (value, record) =>
        `${record?.city ? `${record.city}, ` : ''}${
          record?.state && record.state !== 'Default state' ? `${record.state}, ` : ''
        }${record?.country || '—'}`,
    },
    {
      field: 'offices',
      title: t('translation:merge_profile.label_office_location'),
      required: false,
      isHeader: false,
      render: value => (value as Array<{ uuid: string; name: string }>).map(l => <div key={l.uuid}>{l.name}</div>),
    },
    {
      field: 'timezone',
      title: t('translation:merge_profile.label_time_zone'),
      required: false,
      isHeader: false,
      render: value => value,
    },
    {
      field: 'foreign_languages',
      title: t('translation:merge_profile.label_foreign_language'),
      required: false,
      isHeader: false,
      render: value =>
        (value as Array<{ language: string; level: string }>).map(l => (
          <div key={l.language + l.level}>
            {l.language}: {l.level}
          </div>
        )),
    },
    {
      field: 'sources',
      title: t('translation:merge_profile.label_sources'),
      required: true,
      isHeader: false,
      render: value =>
        (value as Array<{ source: string; additional_source_name?: string; is_main?: boolean }>).map(s => (
          <div key={s.source}>
            {s.additional_source_name ? (
              <>
                <span style={{ color: '#717C8C' }}>
                  {String(s.source).charAt(0).toUpperCase() + String(s.source).slice(1)}:{' '}
                </span>
                {s.additional_source_name}
              </>
            ) : (
              s.source
            )}
            {(value as Array<{ source: string; additional_source_name?: string; is_main?: boolean }>).length > 1 &&
              s.is_main && <strong> ({t('translation:merge_profile.label_main')})</strong>}
          </div>
        )),
    },
    {
      field: 'is_ready_to_relocate',
      title: t('translation:merge_profile.label_ready_to_relocate'),
      required: false,
      isHeader: false,
      render: value => (value ? t('translation:merge_profile.label_yes') : t('translation:merge_profile.label_no')),
    },
    {
      field: 'is_ready_to_business_trips',
      title: t('translation:merge_profile.label_ready_for_business_trips'),
      required: false,
      isHeader: false,
      render: value => (value ? t('translation:merge_profile.label_yes') : t('translation:merge_profile.label_no')),
    },
    {
      field: '_information',
      title: t('translation:merge_profile.label_contact_information'),
      required: false,
      isHeader: true,
      render: value => value,
    },
    {
      field: 'phone',
      title: t('translation:merge_profile.label_phone_number'),
      required: false,
      isHeader: false,
      render: value => (value as string[]).map(p => <div key={p}>{p}</div>),
    },
    {
      field: 'email',
      title: t('translation:merge_profile.label_email_address'),
      required: false,
      isHeader: false,
      render: value => (value as string[]).map(e => <div key={e}>{e}</div>),
    },
    {
      field: 'telegram',
      title: t('translation:merge_profile.label_telegram'),
      required: false,
      isHeader: false,
      render: value => value,
    },
    {
      field: 'social_networks',
      title: t('translation:merge_profile.label_links'),
      required: false,
      isHeader: false,
      render: value =>
        (value as Array<{ link?: string; network?: string }>).map((l, ids) => (
          <div key={(l.link || '') + ids}>
            {l.link}: {l.network}
          </div>
        )),
    },
    {
      field: '_experience',
      title: t('translation:merge_profile.label_experience'),
      required: false,
      isHeader: true,
      render: value => value,
    },
    {
      field: 'specialities',
      title: t('translation:merge_profile.label_specialty'),
      required: true,
      isHeader: false,
      render: value =>
        (value as Array<{ level?: string; speciality?: string | null }>).map(s => (
          <div style={{ whiteSpace: 'nowrap' }}>
            {s.level} {s.speciality}
          </div>
        )),
    },
    {
      field: 'cvs',
      title: t('translation:merge_profile.label_documents'),
      required: false,
      isHeader: false,
      render: value =>
        (value as Array<{ file?: string; filename?: string }>).map(file => <div key={file.file}>{file.filename}</div>),
    },
    {
      field: 'cvs_links',
      title: t('translation:merge_profile.label_cv_links'),
      required: false,
      isHeader: false,
      render: value => (value as string[]).map(l => <div key={l}>{l}</div>),
    },
    {
      field: 'tags',
      title: t('translation:merge_profile.label_tags'),
      required: false,
      isHeader: false,
      render: value => (value as unknown[]).join(', '),
    },
    {
      field: 'description',
      title: t('translation:merge_profile.label_description'),
      required: false,
      isHeader: false,
      render: (value, _, __, _expandedRows, _toggleExpand) => (
        <div>
          <RichText text={value as string} opened />
        </div>
      ),
    },
    {
      field: '_working_experience',
      title: t('translation:merge_profile.label_working_experience'),
      required: false,
      isHeader: true,
      render: value => value,
    },
    {
      field: 'experience',
      title: t('translation:merge_profile.label_working_experience'),
      required: false,
      isHeader: false,
      render: value =>
        (value as Array<Record<string, unknown>>).map((exp, idx) => (
          <div
            key={String(exp.position) + idx}
            className={cn({ 'job-experience__sub-block': idx !== (value as unknown[]).length - 1 })}
          >
            <Typography.Title4>{exp.company as string}</Typography.Title4>
            <Typography.Caption>
              {`${
                exp.worked_from
                  ? moment(exp.worked_from as string).format('MM.YYYY')
                  : t('translation:merge_profile.label_no_date')
              } - ${
                exp.is_current
                  ? t('translation:merge_profile.label_current')
                  : exp.worked_till
                  ? moment(exp.worked_till as string).format('MM.YYYY')
                  : t('translation:merge_profile.label_no_date')
              } | ${exp.position}`}
            </Typography.Caption>
            <RichText text={exp.additional_info as string} opened />
          </div>
        )),
    },
  ];
};

export const INLINE_EDIT_FIELDS = [
  'first_name_latin',
  'last_name_latin',
  'first_name_local',
  'last_name_local',
  'timezone',
  'is_ready_to_relocate',
  'is_ready_to_business_trips',
  'telegram',
];

export const INLINE_SELECT_FIELDS = [
  'offices',
  'foreign_languages',
  'sources',
  'is_ready_to_relocate',
  'is_ready_to_business_trips',
];

export const CHECKBOX_FIELDS = ['sources', 'phone', 'email', 'cvs'];

export const useSelectOptions = (): Record<string, unknown> => {
  const { t } = useTranslation();
  return {
    timezone: TIME_ZONE,
    is_ready_to_relocate: [
      { value: true, label: t('translation:merge_profile.label_yes') },
      { value: false, label: t('translation:merge_profile.label_no') },
    ],
    is_ready_to_business_trips: [
      { value: true, label: t('translation:merge_profile.label_yes') },
      { value: false, label: t('translation:merge_profile.label_no') },
    ],
  };
};
